import { Asset } from "@/api/generated/data-contracts";
import { HttpResponse } from "@/api/generated/http-client";
import { useInboundLabelPrinter } from "@/lib/hooks/printing/labels/useInboundLabelPrinter";
import { useMutationCheckInDevice } from "@/lib/hooks/query/device/useMutationCheckInDevice";
import { useMutationGetDeviceBySerialNumber } from "@/lib/hooks/query/device/useMutationGetDeviceBySerialNumber";
import { useMutationEndCustomerById } from "@/lib/hooks/query/endcustomer/useMutateEndCustomerById";
import { useMutationOrderById } from "@/lib/hooks/query/order/useMutateOrderById";
import { useModal } from "@/lib/modals/useModal";
import {
  scannedDevicesAtom,
  targetOrderIdAtom,
  isAutoPrintAtom,
  ScannedDevice,
} from "@/views/inbound/inboundState";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export function useScannedDevices() {
  const { t } = useTranslation(undefined, { keyPrefix: "inbound.toast" });
  const { showModal } = useModal();

  const [scannedDevices, setScannedDevices] = useAtom(scannedDevicesAtom);
  const [targetOrderId, setTargetOrderId] = useAtom(targetOrderIdAtom);
  const isAutoPrint = useAtomValue(isAutoPrintAtom);

  const getBySerialMutation = useMutationGetDeviceBySerialNumber();
  const checkInMutation = useMutationCheckInDevice();
  const orderMutation = useMutationOrderById();
  const endCustomerMutation = useMutationEndCustomerById();

  const { printLabel } = useInboundLabelPrinter();

  const isFirstDeviceSet = useMemo(
    () => targetOrderId !== undefined,
    [targetOrderId],
  );

  const updateDevice = useCallback(
    (targetDevice: ScannedDevice, updatedFields: Partial<ScannedDevice>) => {
      setScannedDevices((oldDevices) => {
        return oldDevices.map((device) => {
          if (device !== targetDevice) return device;
          return Object.assign(targetDevice, updatedFields);
        });
      });
    },
    [setScannedDevices],
  );

  const handlePrintLabel = useCallback(
    async (device: ScannedDevice) => {
      if (device.orderId === undefined) throw new Error("Order ID is missing");
      const order = await orderMutation.mutateAsync({
        orderId: device.orderId!,
      });
      const endCustomer = await endCustomerMutation.mutateAsync({
        id: order.endCustomerId,
      });
      await printLabel([
        {
          orderId: device.orderId!,
          serialNumber: device.readSerialNumber!,
          eolTrackingNumber: device.eolTrackingNumber!,
          tag: endCustomer.tag!,
        },
      ]);
      updateDevice(device, {
        isPrinted: true,
      });
    },
    [endCustomerMutation, orderMutation, printLabel, updateDevice],
  );

  const checkInDevice = useCallback(
    async (targetDevice: ScannedDevice) => {
      try {
        const serialNumber = targetDevice.readSerialNumber.trim().toLowerCase();
        // ID or serial number is the same
        if (
          scannedDevices.some(
            (d) =>
              d !== targetDevice &&
              ((d.id !== undefined && d.id === targetDevice.id) ||
                d.readSerialNumber?.trim().toLowerCase() === serialNumber),
          )
        ) {
          toast.error(t("this-device-has-already-been-scanned"));
          setScannedDevices((oldDevices) =>
            oldDevices.filter((d) => d !== targetDevice),
          );
          return;
        }

        const checkedInDevice = await checkInMutation.mutateAsync({
          device: targetDevice,
        });

        if (targetOrderId === undefined) {
          setTargetOrderId(checkedInDevice.orderId);
        }

        updateDevice(targetDevice, {
          id: checkedInDevice.id,
          state: "success",
          eolTrackingNumber:
            checkedInDevice.eolTrackingNumber ?? targetDevice.eolTrackingNumber,
          checkInTime: new Date(),
          manufacturer:
            checkedInDevice.manufacturer ?? targetDevice.manufacturer,
          chgManufacturerId:
            checkedInDevice.chgManufacturerId ?? targetDevice.chgManufacturerId,
          productName: checkedInDevice.productName ?? targetDevice.productName,
          orderId: checkedInDevice.orderId ?? targetDevice.orderId,
          classificationCategoryId:
            checkedInDevice.classificationCategoryId ??
            targetDevice.classificationCategoryId,
          extraItems: checkedInDevice.extraItems
            ? checkedInDevice.extraItems
            : undefined,
          isExtra: checkedInDevice.eolBatchItemTypeId !== 1,
        });
      } catch (error) {
        if (error instanceof Response) {
          const response = error as HttpResponse<
            Asset,
            { code?: number; message?: string }
          >;
          const message = response.error.message ?? t("an-error-occurred");
          showModal("Error", { title: message });

          const failure = response.error as {
            assetId?: number;
            orderId?: number;
            eolTrackingNumber?: string;
          };
          updateDevice(targetDevice, {
            id: failure.assetId ?? targetDevice.id,
            orderId: failure.orderId ?? targetDevice.orderId,
            eolTrackingNumber:
              failure.eolTrackingNumber ?? targetDevice.eolTrackingNumber,
            state: "error",
            errorMessage: message,
          });
        } else {
          showModal("Error", { title: t("an-error-occurred") });
          updateDevice(targetDevice, {
            state: "error",
            errorMessage: t("an-error-occurred"),
          });
        }
      }
      if (isAutoPrint) await handlePrintLabel(targetDevice);
    },
    [
      checkInMutation,
      handlePrintLabel,
      isAutoPrint,
      scannedDevices,
      setScannedDevices,
      setTargetOrderId,
      showModal,
      t,
      targetOrderId,
      updateDevice,
    ],
  );

  const handleSingleFound = useCallback(
    async (targetDevice: ScannedDevice, matchedDevices: Asset[]) => {
      const device = matchedDevices[0];
      if (!isFirstDeviceSet || targetOrderId === device.orderId) {
        const order = await orderMutation.mutateAsync({
          orderId: device.orderId,
        });
        const endCustomer = await endCustomerMutation.mutateAsync({
          id: order.endCustomerId,
        });
        if (!endCustomer.tag) {
          let isCanceled = false;
          await showModal("MissingTag", {
            disableClose: true,
            endCustomer,
            onCancel: () => {
              isCanceled = true;
              setScannedDevices((oldDevices) =>
                oldDevices.filter((device) => targetDevice !== device),
              );
            },
          });
          if (isCanceled) return;
        }
        updateDevice(targetDevice, {
          id: device.id,
          stateId: device.stateId,
          manufacturer: device.manufacturer ?? targetDevice.manufacturer,
          chgManufacturerId:
            device.chgManufacturerId ?? targetDevice.chgManufacturerId,
          productName: device.productName ?? targetDevice.productName,
          classificationCategoryId:
            device.classificationCategoryId ??
            targetDevice.classificationCategoryId,
          extraItems: targetDevice.extraItems ?? device.extraItems ?? undefined,
        });
        await checkInDevice(targetDevice);
      } else {
        updateDevice(targetDevice, {
          state: "error",
          orderId: device.orderId,
          stateId: device.stateId,
          manufacturer: device.manufacturer ?? targetDevice.manufacturer,
          productName: device.productName ?? targetDevice.productName,
          classificationCategoryId:
            device.classificationCategoryId ??
            targetDevice.classificationCategoryId,
          eolTrackingNumber:
            device.eolTrackingNumber ?? targetDevice.eolTrackingNumber,
          extraItems:
            targetDevice.extraItems ??
            (device.extraItems ? device.extraItems : undefined),
        });
      }
    },
    [
      checkInDevice,
      endCustomerMutation,
      isFirstDeviceSet,
      orderMutation,
      setScannedDevices,
      showModal,
      targetOrderId,
      updateDevice,
    ],
  );

  const inboundDevice = useCallback(
    async (device: Asset) => {
      const newDevice: ScannedDevice = {
        id: device.id!,
        stateId: device.stateId,
        serialNumber: device.serialNumber!,
        readSerialNumber: device.readSerialNumber!,
        manufacturer: device.manufacturer ?? undefined,
        chgManufacturerId: device.chgManufacturerId ?? undefined,
        productName: device.productName ?? undefined,
        classificationCategoryId: device.classificationCategoryId ?? undefined,
        extraItems: device.extraItems ?? undefined,
        state: "loading",
        isPrinted: false,
        isExtra: false,
        checkInTime: new Date(),
      };
      const order = await orderMutation.mutateAsync({
        orderId: device.orderId,
      });
      const endCustomer = await endCustomerMutation.mutateAsync({
        id: order.endCustomerId,
      });
      if (!endCustomer.tag) {
        let isCanceled = false;
        await showModal("MissingTag", {
          disableClose: true,
          endCustomer,
          onCancel: () => {
            isCanceled = true;
          },
        });
        if (isCanceled) return;
      }
      setScannedDevices((prev) => [...prev, newDevice]);
      await checkInDevice(newDevice);
    },
    [
      checkInDevice,
      endCustomerMutation,
      orderMutation,
      setScannedDevices,
      showModal,
    ],
  );

  const inboundExtraDevice = useCallback(
    async (serialNumber: string) => {
      const newDevice: ScannedDevice = {
        readSerialNumber: serialNumber,
        state: "loading",
        isPrinted: false,
        isExtra: false,
        checkInTime: new Date(),
      };
      setScannedDevices((prev) => [...prev, newDevice]);
      await showModal("InboundAddDevice", {
        disableClose: true,
        device: newDevice,
        onConfirm: (updatedDevice: ScannedDevice) => {
          updateDevice(newDevice, updatedDevice);
          checkInDevice(newDevice);
        },
        onCancel: () => {
          setScannedDevices((oldDevices) =>
            oldDevices.filter((device) => newDevice !== device),
          );
        },
      });
    },
    [checkInDevice, setScannedDevices, showModal, updateDevice],
  );

  const addSerialDevice = useCallback(
    async (serialNumber: string) => {
      const matchedAssets = await getBySerialMutation.mutateAsync({
        serialNumber,
      });
      if (
        matchedAssets.length === 1 &&
        matchedAssets[0].serialNumber === serialNumber
      ) {
        const newAsset: ScannedDevice = {
          readSerialNumber: serialNumber,
          state: "loading",
          isPrinted: false,
          isExtra: false,
          checkInTime: new Date(),
        };
        setScannedDevices((prev) => [...prev, newAsset]);
        await handleSingleFound(newAsset, matchedAssets);
      } else {
        await showModal("InspectDeviceSearch", { serialNumber });
      }
    },
    [getBySerialMutation, handleSingleFound, setScannedDevices, showModal],
  );

  const addManualDevice = useCallback(async () => {
    await showModal("InspectDeviceSearch", { serialNumber: "" });
  }, [showModal]);

  return {
    scannedDevices,
    updateDevice,
    addManualDevice,
    addSerialDevice,
    inboundDevice,
    inboundExtraDevice,
  };
}
